import { useMemo } from 'react';
import naughtyWords from 'naughty-words';
import { customBadWords } from '../json/customBadWords'; // adjust path as needed

/**
 * Normalises a string for comparison:
 * lower-case, collapse whitespace, strip common leet-speak substitutions.
 */
const normalise = (str) =>
  str
    .toLowerCase()
    .replace(/[*@#$!]/g, '')        // strip masking chars
    .replace(/1/g, 'i')
    .replace(/3/g, 'e')
    .replace(/0/g, 'o')
    .replace(/4/g, 'a')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Build a flat Set of every bad word we care about.
 * naughty-words exports an object keyed by language code, e.g. { en: [...], hi: [...] }.
 * We pull English + Hindi (covers the Gujarati-transliterated words) plus every
 * custom phrase from customBadWords.js.
 */
const buildBadWordSet = () => {
  const langs = ['en', 'hi', 'ar']; // add more language codes as needed
  const fromPackage = langs.flatMap((lang) => naughtyWords[lang] ?? []);
  const all = [...fromPackage, ...customBadWords];
  return new Set(all.map(normalise));
};

// Build once at module load – no repeated work per render
const BAD_WORD_SET = buildBadWordSet();

/**
 * Returns true if `text` contains any bad word from the combined list.
 * Checks:
 *   1. Whole-phrase matches (for multi-word custom phrases)
 *   2. Individual word-token matches (for single-word profanity)
 */
const containsBadWord = (text) => {
  if (!text) return false;

  const normText = normalise(text);

  // 1. Whole-phrase match – catches multi-word custom phrases
  for (const bad of BAD_WORD_SET) {
    if (normText.includes(bad)) return true;
  }

  // 2. Token-level match – faster short-circuit for single words
  const tokens = normText.split(/\s+/);
  for (const token of tokens) {
    if (BAD_WORD_SET.has(token)) return true;
  }

  return false;
};

/**
 * useBadWordDetection
 *
 * @param {string} text  – the current textarea / input value
 * @returns {{ hasBadWord: boolean }}
 *
 * Usage:
 *   const { hasBadWord } = useBadWordDetection(comment);
 */
const useBadWordDetection = (text) => {
  const hasBadWord = useMemo(() => containsBadWord(text), [text]);
  return { hasBadWord };
};

export default useBadWordDetection;